import { zodResolver } from '@hookform/resolvers/zod'
import { SaveOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Divider, Stack } from '@mui/material'
import { DateTime, Interval } from 'luxon'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ALL_WEEKDAYS } from 'rrule/dist/esm/weekday'

import { ZONED_TODAY } from '@/constants/dateTime'
import { useAlertContext } from '@/contexts/AlertContext'
import { useAnalytics } from '@/features/googleAnalytics/hooks/useAnalytics'
import { AvailabilityWireSelectionSection } from '@/features/site/components/availability/form/AvailabilityWireSelectionSection'
import DeleteServiceRuleButton from '@/features/site/components/availability/serviceRule/form/DeleteServiceRuleButton'
import { ServiceRuleConditionSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleConditionSection'
import { ServiceRuleConfigurationInfoSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleConfigurationInfoSection'
import { ServiceRuleDatesSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleDatesSection'
import { ServiceRuleExceptionDatesSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleExceptionDatesSection'
import { ServiceRuleRecurrenceSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleRecurrenceSection'
import { ServiceRuleStatusSection } from '@/features/site/components/availability/serviceRule/form/ServiceRuleStatusSection'
import { getSelectedDays } from '@/features/site/components/availability/utils'
import { useCreateServiceRuleMutation } from '@/features/site/hooks/useCreateServiceRuleMutation'
import { useEditServiceRuleMutation } from '@/features/site/hooks/useEditServiceRuleMutation'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { ExceptionDate, ServiceRuleForm } from '@/features/site/types/serviceRuleForm'
import { getServiceRuleFormSchema } from '@/features/site/types/serviceRuleForm'
import { AVAILABILITY_STATUSES } from '@/features/unavailability/types/availabilityStatus'
import type {
  CreateServiceRuleRequest,
  EditServiceRuleRequest,
  ServiceRule,
} from '@/features/unavailability/types/serviceRule'
import { convertToTimeZoneDateTime } from '@/utils/time'

interface ServiceRuleDetailsFormProps {
  onClose: () => void
  serviceRule?: ServiceRule
  marketProgram: MarketProgram
  contractPrice?: number
  siteTimezone: string
  siteLocation: string
  siteUuid: string
}

const CustomDivider = () => <Divider style={{ marginBottom: '16px', marginTop: '16px' }} />
export const ServiceRuleDetailsForm = ({ contractPrice = 0, ...props }: ServiceRuleDetailsFormProps) => {
  const { t } = useTranslation()
  const { sendAnalyticsEvent } = useAnalytics()
  const { onClose } = props
  const { pushAlert } = useAlertContext()
  const isAFRR = props.marketProgram.type === 'afrr-up' || props.marketProgram.type === 'afrr-down'
  const isDeleteAllowed =
    props.serviceRule && isAFRR && props.serviceRule?.status !== AVAILABILITY_STATUSES.active.value

  const form = useForm<ServiceRuleForm>({
    mode: 'onBlur',
    defaultValues: {
      id: props.serviceRule?.id ?? undefined,
      startDate: props.serviceRule?.startDate
        ? convertToTimeZoneDateTime(props.serviceRule.startDate.toString(), props.siteTimezone)
        : ZONED_TODAY(props.siteTimezone).plus({ days: 1 }),
      endDate: props.serviceRule?.endDate
        ? convertToTimeZoneDateTime(props.serviceRule.endDate.toString(), props.siteTimezone)
        : ZONED_TODAY(props.siteTimezone).plus({ days: 2 }),
      startTime: props.serviceRule?.startTime
        ? DateTime.fromISO(props.serviceRule.startTime, { zone: props.siteTimezone })
        : ZONED_TODAY(props.siteTimezone).startOf('day').startOf('minute'),
      endTime: props.serviceRule?.endTime
        ? DateTime.fromISO(props.serviceRule.endTime, { zone: props.siteTimezone })
        : ZONED_TODAY(props.siteTimezone).endOf('day').startOf('minute'),
      exceptionDates: props.serviceRule?.exceptionDates
        ? convertExceptionDatesToForm(props.serviceRule.exceptionDates, props.siteTimezone)
        : ([] as ExceptionDate[]),
      recurringDays: props.serviceRule?.recurrence ? [...getSelectedDays(props.serviceRule?.recurrence)] : [],
      selectedResources: props.serviceRule?.analogWires?.map((resource) => resource.id) ?? [],
      location: props.siteLocation,
      limitValue: props.serviceRule?.limitValue ?? contractPrice,
      status: props.serviceRule?.status,
    },
    resolver: zodResolver(getServiceRuleFormSchema(t)),
  })

  useWatch({
    control: form.control,
  })

  const { createServiceRule } = useCreateServiceRuleMutation()
  const { editServiceRule } = useEditServiceRuleMutation()

  function convertToRequest(formValues: ServiceRuleForm): CreateServiceRuleRequest | EditServiceRuleRequest | null {
    const serviceId = props.marketProgram.id

    const startDateString = formValues.startDate.toUTC().toISO({ suppressMilliseconds: true, includeOffset: false })
    const endDateString = formValues.endDate.toUTC().toISO({ suppressMilliseconds: true, includeOffset: false })

    const exceptionDates = convertExceptionDatesToRequest(formValues.exceptionDates)

    let recurrence = 'FREQ=DAILY'
    if (formValues.recurringDays.length !== ALL_WEEKDAYS.length) {
      const recurringDays = formValues.recurringDays?.join(',')
      recurrence = 'FREQ=DAILY;BYDAY=' + recurringDays
    }

    return {
      id: formValues.id ?? undefined,
      serviceId: serviceId,
      startDate: startDateString ?? '',
      endDate: endDateString ?? '',
      startTime: formValues.startTime.toFormat('HH:mm'),
      endTime: formValues.endTime.toFormat('HH:mm'),
      exceptionDates: exceptionDates,
      recurrence: recurrence,
      limitValue: contractPrice,
      relayWires: !isAFRR ? formValues.selectedResources.map((id) => ({ id })) : [],
      analogWires: isAFRR ? formValues.selectedResources.map((id) => ({ id })) : [],
      location: props.siteLocation,
      status: formValues.status ?? undefined,
    }
  }

  const handleSubmit = async () => {
    const serviceRuleRequest = convertToRequest(form.getValues())

    if (!serviceRuleRequest) {
      form.trigger() // Trigger validation to show errors
      return
    }

    if (!props.serviceRule) {
      await createServiceRule({
        customerUuid: props.siteUuid,
        serviceId: props.marketProgram.id,
        serviceRule: serviceRuleRequest as CreateServiceRuleRequest,
      }).then(() => {
        sendAnalyticsEvent({
          name: 'availability_created',
          dimensions: {
            customer_uuid: props.siteUuid,
            availability_type: 'availability',
            market_program: props.marketProgram.type,
          },
        })
      })
      pushAlert({
        message: t('customer_details.tabs.availability.availability_details.dialog.success.create_message'),
        severity: 'success',
      })
    } else {
      await editServiceRule({
        customerUuid: props.siteUuid,
        serviceRule: serviceRuleRequest as EditServiceRuleRequest,
      })
      pushAlert({
        message: t('customer_details.tabs.availability.availability_details.dialog.success.edit_message'),
        severity: 'success',
      })
    }

    handleClose()
  }

  const handleClose = () => {
    onClose()
    form.reset()
  }

  return (
    <Stack spacing={2}>
      <FormProvider {...form}>
        <ServiceRuleRecurrenceSection />
        <CustomDivider />
        <ServiceRuleDatesSection timezone={props.siteTimezone} />
        <CustomDivider />
        <ServiceRuleExceptionDatesSection timezone={props.siteTimezone} />
        <CustomDivider />
        <ServiceRuleConditionSection contractPrice={contractPrice} marketProgram={props.marketProgram} />
        <CustomDivider />
        <AvailabilityWireSelectionSection customerUuid={props.siteUuid} marketProgram={props.marketProgram} />
        <CustomDivider />
        <ServiceRuleStatusSection />
        <ServiceRuleConfigurationInfoSection
          contractPrice={contractPrice}
          customerTimeZone={props.siteTimezone}
          marketProgram={props.marketProgram}
        />
      </FormProvider>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<SaveOutlined />} variant={'contained'} onClick={form.handleSubmit(handleSubmit)}>
            {t('common.button.save')}
          </Button>
          <Button startIcon={<CloseIcon />} variant={'outlined'} onClick={handleClose}>
            {t('common.button.cancel')}
          </Button>
        </Stack>
        {isDeleteAllowed && (
          <DeleteServiceRuleButton
            serviceId={props.marketProgram.id}
            serviceRuleId={props.serviceRule?.id}
            siteUuid={props.siteUuid}
            onDeleteSuccess={handleClose}
          />
        )}
      </Stack>
    </Stack>
  )
}

function convertExceptionDatesToRequest(formExceptionDates: ExceptionDate[]) {
  const exceptionDates = [] as string[]
  for (const exceptionDate of formExceptionDates) {
    if (exceptionDate.range) {
      const intervals = Interval.fromDateTimes(
        exceptionDate.startDate.startOf('day'),
        exceptionDate.endDate!.endOf('day'),
      )
        .splitBy({ day: 1 })
        .map((d) => d.start?.toISO({ suppressMilliseconds: true, includeOffset: false }))
      for (const date of intervals) {
        exceptionDates.push(date!)
      }
    } else {
      exceptionDates.push(exceptionDate.startDate.toISO({ suppressMilliseconds: true, includeOffset: false })!)
    }
  }
  return exceptionDates
}

function convertExceptionDatesToForm(exceptionDates: string[], customerTimezone: string): ExceptionDate[] {
  const formExceptionDates: ExceptionDate[] = []
  const sortedExceptionDates = [...exceptionDates].toSorted((a, b) => a.localeCompare(b))

  let temp: string[] = []
  for (let i = 0; i < sortedExceptionDates.length; i++) {
    temp.push(sortedExceptionDates[i])

    if (sortedExceptionDates[i + 1]) {
      const currentDate = DateTime.fromISO(sortedExceptionDates[i], { zone: customerTimezone })
      const nextDate = DateTime.fromISO(sortedExceptionDates[i + 1], { zone: customerTimezone })

      if (currentDate.plus({ days: 1 }).hasSame(nextDate, 'day')) {
        continue
      }
    }

    if (temp.length > 1) {
      formExceptionDates.push({
        range: true,
        startDate: DateTime.fromISO(temp[0], { zone: customerTimezone }),
        endDate: DateTime.fromISO(temp[temp.length - 1], { zone: customerTimezone }),
      })
    } else {
      formExceptionDates.push({
        range: false,
        startDate: DateTime.fromISO(temp[0], { zone: customerTimezone }),
      })
    }

    temp = []
  }

  return formExceptionDates
}
